import React, { useState, useEffect, useCallback } from 'react';
import Paper from "@mui/material/Paper";
import DtvTheme, { colorPalette } from "@directv-digital/dds.ui.dtv-theme";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvButton from "@directv-digital/dds.ui.dtv-button";
import DtvText from "@directv-digital/dds.ui.dtv-text";
import DtvSpinner from "@directv-digital/dds.ui.dtv-spinner";
import DtvGrid from "@directv-digital/dds.ui.dtv-grid";
import DtvAvatar from "@directv-digital/dds.ui.dtv-avatar";
import ErrorDisplay from "@directv-digital/dds.ui.error-display";
import CardMedia from "@mui/material/CardMedia";
import { getDmaCookies } from "@directv-digital/dds.services.cookie-service";
import Tooltip from "@mui/material/Tooltip";

// import { newCountyMockRes, newOTTChannelsMockRes, newDTVChannelsMockRes } from "./mockData";
import { handleLocalRsnChannelFilter } from 'lib/service/channels/get-local-national-dma-eligible-channels';
import ZipcodeModule, { ZipcodeModuleProps } from "src/components/video-lab/zipcode-module";
import { LocalRsnApiResponse } from "@directv-digital/dds.apis.local-rsn-chnls";
import { handleRemoveMyHomeTeamDuplicates } from "src/utils/RemoveMyHomeTeamDuplicates";
import { getAnalyticsAttrs } from '@directv-digital/dds.services.analytics-service';

export type RsnLookupToolProps = {
  id: string;
  type: string;
  backgroundColor: string;
  header: any;
  subheading: any;
  nationalChannelsHeader: any,
  regionalChannelsHeader: any,
  zipCodeLabel: string;
  countyLabel: string;
  searchBtn: any;
  gridHeaders: any;
  packageKeys: any;
  legalTextRsnTier2: any;
  legalTextRsnTier3: any;
  zipCodeErrorMsg: string;
  countySearchErrorMsg: string;
  channelSearchErrorMsg: string;
  noChannelResultsMsg: string;
  aboveTheFold?: boolean;
  nationalChannels?: any;
  variant?: string;
  queryParams?: any;
  genreLocalChannelMapping?: any;
  nationalRsnVariantLegal?: any;
  zipcodeModuleConfig?: ZipcodeModuleProps["ciConfig"];
};

// eslint-disable-next-line complexity
export function RsnLookupTool(props: RsnLookupToolProps) {
  try {
    const {
      id: componentId,
      genreLocalChannelMapping,
      queryParams,
      variant,
      zipcodeModuleConfig
    } = props;
    const [apiError, setApiError] = useState<boolean>(false);
    const [countySearchError, setCountySearchError] = useState<boolean>(false);

    const [tierTwoFlag, setTierTwoFlag] = useState<boolean>(false);
    const [tierThreeFlag, setTierThreeFlag] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState(false);
    const [showRsnGrid, setShowRsnGrid] = useState(false);
    const [regionalChannels, setRegionalChannels] = useState<any>([]);
    const [filteredNationalChannels, setFilteredNationalChannels] = useState<any>([]);

    const natVariantShowSignaturePacks = variant !== "nationalAndRsn" || queryParams?.showsignaturepackages === "true";

    useEffect(() => {
      const flag = Boolean(regionalChannels?.length > 0);
      setShowRsnGrid(flag);
    }, [regionalChannels]);

    const { breakpoints } = DtvTheme;
    const styles = {
      jetColor: {
        color: "#102641"
      },
      componentPadding: {
        display: "block",
        backgroundColor: props?.backgroundColor ? props?.backgroundColor : "#FEFEFE",
        padding: {
          xs: "50px 10px 30px 10px",
          sm: "86px 32px 40px 32px",
          md: "80px 96px 50px 96px",
          lg: "80px 140px 50px 140px"
        },
        minHeight: {
          xs: "100vh",
          sm: "500px",
          md: "700px"
        }
      },
      headings: {
        container: {
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          margin: {
            xs: '0px 10px',
            sm: '0px'
          },
          marginBottom: {
            xs: "28px",
            sm: "32px",
            md: "28px"
          }
        },
        header: {
          container: {
            width: "100%",
            textAlign: "center",
            marginBottom: "12px"
          },
          textStyles: {
            fontSize: {
              xs: "28px",
              sm: "36px",
              md: "44px"
            },
            fontWeight: "500",
            lineHeight: "120%", /* 43.2px */
            letterSpacing: "-0.72px"
          }
        },
        subheading: {
          container: {
            width: "100%",
            textAlign: "center",
            margin: "32px 0px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            [breakpoints.down(breakpoints.values.sm)]: {
              flexDirection: "column"
            }
          },
          textStyles: {
            fontSize: {
              xs: "20px !important",
              md: "24px !important"
            },
            fontWeight: "400",
            lineHeight: "130%", /* 26px */
            letterSpacing: "-0.4px"
          }
        }
      },

      gridConfig: {
        container: {
          display: "flex",
          flexFlow: "row wrap",
          justifyContent: "center",
          alignContent: "center",
          width: '100%',
          marginBottom: {
            xs: "16px",
            sm: "24px",
            md: "32px"
          }
        },
        spinnerPos: {
          display: "flex",
          justifyContent: "center",
          width: "100%",
          height: "100%"
        },
        headings: {
          containers: {
            height: "68px",
            border: "1px solid #D1D1D1",
            display: "flex",
            textAlign: "center",
            alignItems: "center",
            justifyContent: "center"
          },
          col1: {
            color: props?.gridHeaders?.[0]?.color ? props?.gridHeaders?.[0]?.color : "#FFF",
            backgroundColor: props?.gridHeaders?.[0]?.bgColor ? props?.gridHeaders?.[0]?.bgColor : "#102641",
            marginBottom: '0 !important'
          },
          col2: {
            color: props?.gridHeaders?.[0]?.color ? props?.gridHeaders?.[1]?.color : "#000",
            backgroundColor: props?.gridHeaders?.[1]?.bgColor ? props?.gridHeaders?.[1]?.bgColor : "#EAF1FA",
            borderLeft: "0px",
            borderRight: "0px"
          },
          col3: {
            color: props?.gridHeaders?.[2]?.color ? props?.gridHeaders?.[2]?.color : "#000",
            backgroundColor: props?.gridHeaders?.[2]?.bgColor ? props?.gridHeaders?.[2]?.bgColor : "#F5F5F5"
          },
          commonHeadingText: {
            fontFamily: "PFDinReg",
            fontSize: '20px !important',
            lineHeight: '130%', /* 26px */
            letterSpacing: '-0.48px',
            [breakpoints.up(breakpoints.values.md)]: {
              fontSize: '24px !important'
            },
            fontWeight: { sm: 'bold' },
            padding: {
              xs: "0 12px",
              sm: "0 16px",
              md: "0 20px"
            },
            marginBottom: '0',
            wordWrap: 'break-word'
          },
          subheadings: {
            width: "100%",
            flexBasis: "100%",
            height: "36px",
            textAlign: "center",
            borderTop: "1px solid #D1D1D1",
            borderBottom: "1px solid #D1D1D1",
            padding: "3px 0",
            backgroundColor: "#F5F5F5",
            color: "#555555",
            fontSize: {
              xs: "20px !important",
              sm: "24px !important"
            }
          }
        },
        cell: {
          height: "100%",
          minHeight: "133px",
          border: "1px solid #D1D1D1",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center"
        },

        tr: {
          minHeight: {
            xs: "133px",
            sm: "155px"
          },
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center"
        },

        networkContainer: {
          padding: {
            xs: "10px 3px",
            sm: "10px 5px",
            md: "18px 10px"
          }
        },
        networkItem: {
          fontSize: {
            xs: '14px !important',
            sm: '16px !important',
            md: '20px !important'
          }
        },
        rsnChlLogo: {
          display: "inline-flex"

        },

        teamsContainer: {
          padding: {
            xs: "10px 3px",
            sm: "10px 5px",
            md: "15px 10px"
          }
        },
        teamsItem: {
          fontFamily: "PFDinReg",
          display: "block",
          marginTop: {
            xs: "4px",
            sm: "6px"
          },
          fontSize: {
            xs: '14px !important',
            sm: '20px !important',
            md: '24px !important'
          }
        },
        teamsTooltip: {
          fontSize: "20px",
          fontWeight: 500,
          backgroundColor: "#102641",
          display: 'inline-block',
          height: "30px",
          width: "30px",
          borderRadius: '50px',
          position: "relative",
          top: "-0.6em",
          verticalAlign: "middle",
          left: "5px",
          cursor: "default",
          color: "#FFF"
        },

        packagesContainer: {
          padding: {
            xs: "12px 8px 12px 8px",
            sm: "12px 58px 12px 58px",
            md: "24px 50px 24px 58px"
          }
        },
        pkgLink: {
          display: "block",
          position: "relative",
          "&:hover": {
            textDecoration: "none !important"
          },
          [breakpoints.down(breakpoints.values.sm)]: {
            fontSize: "14px !important",
            marginTop: "11px",
            top: "-11px"
          },
          [breakpoints.between(breakpoints.values.sm, breakpoints.values.md)]: {
            fontSize: "16px !important",
            marginTop: "16px",
            top: "-16px"
          },
          [breakpoints.up(breakpoints.values.md)]: {
            fontSize: "24px !important",
            marginTop: "8px",
            top: "-8px"
          }
        },
        borderHighlight: {
          borderBottom: "1px solid #999999"
        }
      },
      legalTextStyles: {
        fontSize: "12px !important",
        fontWeight: '400',
        lineHeight: "130%", /* 15.6px */
        textAlign: "left",
        color: "#102641",
        margin: "8px 0 0 0"
      },
      tooltipStyles: {
        opacity: "1",
        transform: "none",
        transition: "opacity 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, transform 133ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
        transformOrigin: "left center",
        backgroundColor: "rgba(97, 97, 97, 0.92)",
        borderRadius: "4px",
        color: colorPalette.white.main,
        padding: "4px 8px",
        fontSize: "14px",
        maxWidth: "fit-content",
        margin: "0",
        wordWrap: "break-word",
        fontWeight: 400,
        fontFamily: "roboto-regular",
        position: "relative",
        marginLeft: "0px!important"
      },
      tooltipArrow: {
        position: "absolute",
        top: "0px",
        transform: "translate3d(0px, 8.8px, 0px)",
        left: "-10px",
        marginLeft: "-0.71em",
        height: "1em",
        width: "0.71em",
        overflow: "hidden",
        boxSizing: "border-box",
        color: "rgba(97, 97, 97, 0.9)",
        '::before': {
          transformOrigin: '100% 100%'
        }
      }
    };

    const setLoadingLocalRsnChannels = useCallback(() => {
      setIsLoading(true);
    }, []);

    const addLocalRsnChnls = useCallback((channels: LocalRsnApiResponse | null) => {
      const { rsnChannels = [] } = channels;
      setApiError(false);

      console.log("rsn tool got channels", rsnChannels);

      const dmas = getDmaCookies();
      const myHomeTeamChannels = handleLocalRsnChannelFilter(rsnChannels, genreLocalChannelMapping, "myhometeam", dmas?.currentDma, dmas?.nielsenDma);
      const newChannels = handleRemoveMyHomeTeamDuplicates(
        [...myHomeTeamChannels, ...(props?.nationalChannels || [])]
      )
        .sort((a: any, b: any) => a?.channelNameLabel?.localeCompare(b?.channelNameLabel));

      const newNatChnls = newChannels?.filter(channel => channel?.categoryId !== "RSN") || [];
      const newRsnChnls = newChannels?.filter(channel => channel?.categoryId === "RSN") || [];

      setFilteredNationalChannels(newNatChnls);
      const tempChannels = (variant === "nationalAndRsn" ? newRsnChnls : rsnChannels);

      if(tempChannels?.length > 0) {
        setRegionalChannels(tempChannels);
        setTierTwoFlag(tempChannels.some(channel => channel.rsntier === 2));
        setTierThreeFlag(tempChannels.some(channel => channel.rsntier === 3));
      } else {
        setRegionalChannels([]);
      }
      setIsLoading(false);
    }, [genreLocalChannelMapping, variant]);

    const handleCountyApiError = useCallback(() => {
      setCountySearchError(true)
    }, []);

    const handleLocalRsnApiError = useCallback(error => {
      setApiError(true);
      setIsLoading(false);
      console.error("LocalRsn Api Error", error as string);
    }, []);

    return (
      <DtvBox
        id={componentId}
        sx={{
          ...styles.componentPadding,
          position: "relative" // set context for DtvSpinner w/ onParent=true
        }}
      >
        <>
          <DtvBox sx={{ ...styles.headings.container }}>
            {props?.header &&
              <DtvBox sx={{ ...styles.headings.header.container }}>
                <DtvText
                  color={props?.header?.[0]?.color ? props?.header?.[0]?.color : props?.header?.color}
                  component="p"
                  content={props?.header?.[0]?.content ? props?.header?.[0]?.content : props?.header?.content}
                  variant={props?.header?.[0] ? props?.header?.[0]?.variant : props?.header?.variant}
                  cta={{
                    label: props?.header?.[0]?.cta?.label || props?.header?.cta?.label,
                    legalUrl: props?.header?.[0]?.cta?.legalUrl || props?.header?.cta?.legalUrl,
                    position: props?.header?.[0]?.cta?.position || props?.header?.cta?.position,
                    target: props?.header?.[0]?.cta?.target || props?.header?.cta?.target
                  }}
                  sx={{
 ...styles.headings.header.textStyles,
fontFamily: "PFDinMed"
}}
                  tooltip={{
                    content: props?.header?.[0]?.tooltip?.content || props?.header?.tooltip?.variant,
                    position: props?.header?.[0]?.tooltip?.position || props?.header?.tooltip?.variant,
                    mode: props?.header?.[0]?.tooltip?.mode || props?.header?.tooltip?.variant,
                    sx: {
                      color: props?.header?.[0]?.color || props?.header?.color,
                      fontSize: props?.header?.[0]?.tooltip?.fontSize || props?.header?.tooltip?.fontSize
                    }
                  }}
                />
              </DtvBox>
            }
            <ZipcodeModule
              id="rsn-lookupzip-module"
              mode="light"
              position="center"
              onCountyApiError={handleCountyApiError}
              onLocalRsnChnlsApiError={handleLocalRsnApiError}
              onLocalRsnChnlsConfirmed={addLocalRsnChnls}
              onSearchButtonClick={setLoadingLocalRsnChannels}
              isConditional={!!queryParams?.zipCode}
              // Generated by Copilot- End - 2026-04-15 14:21:57
              ciConfig={zipcodeModuleConfig}
              localRsnApiOptions={{
 stream: true,
satellite: false
}}
              // Generated by Copilot- Start - 2026-04-15 14:21:57
              initData={queryParams?.zipCode ? { zipcode: queryParams?.zipCode } : {}}
            />
          </DtvBox>
          {!showRsnGrid && countySearchError && !isLoading && (
            <DtvText
              color="#102641"
              component="p"
              gutterBottom={true}
              id="component-error"
              variant="smalltext"
              content={props?.countySearchErrorMsg ? props?.countySearchErrorMsg :
                "Error retrieving counties by zip code. Please try again later."}
              sx={{
                fontSize: "14px",
                textAlign: "center",
                mt: {
                  xs: "8px",
                  sm: "12px",
                  md: "8px"
                },
                mb: {
                  xs: "30px",
                  sm: "36px",
                  md: "50px"
                }
              }}
            />
          )}

          {isLoading &&
            <DtvBox sx={ regionalChannels?.length > 0 && { ...styles.gridConfig.spinnerPos }}>
              <DtvSpinner msg={""} open={true} onParent={true}/>
            </DtvBox>
          }
          {filteredNationalChannels && filteredNationalChannels?.length > 0 && (
            <DtvBox style={{
 display: "flex",
flexDirection: "column",
justifyContent: "center",
alignItems: "center"
}}>
              <DtvText
                color={props?.nationalChannelsHeader?.[0]?.color || "#102641"}
                content={props?.nationalChannelsHeader?.[0]?.content || "National Channels"}
                variant={props?.nationalChannelsHeader?.[0]?.variant || "body1medium"}
              />
              <DtvBox sx={{
 display: "flex",
alignItems: "center",
justifyContent: "center",
marginBottom: {
 xs: "16px",
sm: "24px",
md: "24px"
},
flexWrap: "wrap"
}}>
              {filteredNationalChannels?.map((channel, index) => (
                  <Tooltip
                  describeChild
                  enterTouchDelay={0}
                  key={index}
                  placement="bottom"
                  title={channel?.channelNameLabel}
                  PopperProps={{
                    disablePortal: true,
                    modifiers: [{
                      name: 'preventOverflow',
                      enabled: true,
                      phase: 'main'
                    }, {
                      name: 'flip',
                      enabled: true,
                      phase: 'main'
                    }, {
                      name: 'offset',
                      options: {
                        offset: [0, -20]
                      }
                    }],
                    sx: { width: 'unset !important' }
                  }}
                  componentsProps={{
                    tooltip: { sx: styles.tooltipStyles },
                    arrow: { sx: styles.tooltipArrow  }
                  }}
                  sx={{
                    "& #popper[data-popper-escaped]": {
                      visibility: 'hidden'
                    }
                  }}>
                  {channel?.channelLogoPath && (
                    <DtvBox
                      id={`icon-container-${channel?.categoryId}-${index}`}
                      tabIndex={0}
                      // {...(!props?.displayGenrePacks && {
                      //   component : "a",
                      //   role : "button"
                      // })}
                      sx={{
                        backgroundColor: "transparent",
                        borderRadius: "50%",
                        position: "relative",

                        padding: "0px",
                        margin: "8px 9px 8px 9px",
                        "&:focus-visible": {
                          outline: "2px solid #3885E1",
                          outlineOffset: "-2px"
                        }
                      }}>
                        <CardMedia
                          alt={channel?.channelNameLabel || "channel logo icon"}
                          aria-labelledby={`add-on-icon-container-${channel?.category}-${index}`}
                          component={"img"}
                          key={index}
                          loading="lazy"
                          src={`https://www.directv.com/${channel.channelLogoPath}`}
                          sx={{
                            paddingTop: "1.3px", // eyeballed to center svg exactly
                            fontSize: "8px", // for error text
                            color: colorPalette.white.main,
                            contentVisibility: "auto",
                            position: "relative",
                            zIndex: 2,
                            width: "80px !important",
                            height: "80px !important"
                          }}
                          onError={e => {
                            console.log("channel icon not found", channel);
                            e.currentTarget.parentElement.style.display = "none";
                          }}
                        />
                    </DtvBox>
                  )}
                </Tooltip>
                ))}
              </DtvBox>
            </DtvBox>
            )
          }
          <>
            {!isLoading && !countySearchError && <>
              {variant === "nationalAndRsn" && (
                <DtvBox sx={{
 display: "flex",
justifyContent: "center"
}}>
                    <DtvText
                      color={props?.nationalChannelsHeader?.[0]?.color || "#102641"}
                      content={props?.regionalChannelsHeader?.[0]?.content || "Regional Sports Networks"}
                      variant={props?.regionalChannelsHeader?.[0]?.variant || "body1medium"}
                      sx={{
 marginBottom: {
 xs: "8px",
sm: "12px",
md: "12px"
}
}}
                    />
                  </DtvBox>)}
              {regionalChannels?.length > 0 ? (
                <>
                  <DtvGrid container={true} sx={{ ...styles.gridConfig.container }}>

                    <DtvGrid item container={true} sm={7} xs={12} sx={{
 maxWidth: '100% !important',
flexBasis: '100% !important'
}}>
                      <DtvGrid item={true} sm={natVariantShowSignaturePacks ? 4 : 6} xs={5} sx={{
 maxWidth: natVariantShowSignaturePacks ? "33.33%" : "50%",
flexBasis: natVariantShowSignaturePacks ? "33.33%" : "50%"
}}>
                        <Paper
                          elevation={0}
                          square={true}
                          sx={{
                            ...styles.gridConfig.headings.containers,
                            ...styles.gridConfig.headings.col1
                          }}
                        >
                          <DtvText
                            component="p"
                            content={props?.gridHeaders?.[0]?.label || "Network"}
                            gutterBottom={true}
                            sx={{ ...styles.gridConfig.headings.commonHeadingText }}
                            variant="body1"
                          />
                        </Paper>
                      </DtvGrid>
                      <DtvGrid item={true} sm={natVariantShowSignaturePacks ? 4 : 6} xs={5} sx={{
 maxWidth: natVariantShowSignaturePacks ? "33.33%" : "50%",
flexBasis: natVariantShowSignaturePacks ? "33.33%" : "50%"
}}>
                        <Paper
                          elevation={0}
                          square={true}
                          sx={{
                            ...styles.gridConfig.headings.containers,
                            ...styles.gridConfig.headings.col2
                          }}
                        >
                          <DtvText
                            component="p"
                            content={props?.gridHeaders?.[1]?.label.replace("/", "/<wbr></wbr>") || "Affiliate / Teams"}
                            gutterBottom={true}
                            sx={{ ...styles.gridConfig.headings.commonHeadingText }}
                            variant="body1"
                          />
                        </Paper>
                      </DtvGrid>
                      {natVariantShowSignaturePacks &&
                      <DtvGrid item={true} sm={4} xs={5} sx={{
 maxWidth: "33.33%",
flexBasis: "33.33%"
}}>
                        <Paper
                          elevation={0}
                          square={true}
                          sx={{
                            ...styles.gridConfig.headings.containers,
                            ...styles.gridConfig.headings.col3
                          }}
                        >
                          <DtvText
                            component="p"
                            content={props?.gridHeaders?.[2]?.label || "Available Packages"}
                            gutterBottom={true}
                            sx={{ ...styles.gridConfig.headings.commonHeadingText }}
                            variant="body1"
                          />
                        </Paper>
                      </DtvGrid>}
                    </DtvGrid>

                    {regionalChannels.map((row, index) => {
                      const exclusivelyOnHeader = (row?.isSatellite && !row?.isStream) || (!row?.isSatellite && row?.isStream);
                      const subheadingStyles = exclusivelyOnHeader ? styles.gridConfig.borderHighlight : {};
                      const isCombineChannel = row?.isSatellite && row?.isStream;
                      const pkgs = props?.packageKeys?.filter(key => row?.packageNames?.some(pkg => pkg.includes(key.label)));

                      return(
                        <DtvGrid container={true} item={true} key={index} sm={7} xs={12} sx={{
 ...subheadingStyles,
maxWidth: '100% !important',
flexBasis: '100% !important',
borderLeft: "1px solid #D1D1D1",
borderRight: "1px solid #D1D1D1"
}}>

                          {/* {exclusivelyOnHeader &&
                            <>
                              <DtvText
                                variant="subhead2"
                                content={`Exclusive on ${row?.isSatellite ? 'Satellite' : 'Internet'}`}
                                sx={{
                                  ...styles.gridConfig.headings.subheadings, fontFamily: "PFDinReg"
                                }}
                              />
                            </>
                          } */}
                          <DtvGrid item={true} sm={natVariantShowSignaturePacks ? 4 : 6} xs={5} sx={{
 maxWidth: natVariantShowSignaturePacks ? "33.33%" : "50%",
flexBasis: natVariantShowSignaturePacks ? "33.33%" : "50%"
}}>
                            <Paper
                              elevation={0}
                              square={true}
                              sx={index === 0 ?
                                {
 ...styles.gridConfig.cell,
                                    ...styles.gridConfig.networkContainer,
                                    borderLeft: "0px"
                                  } :
                                index === regionalChannels.length - 1 ?
                                  {
 ...styles.gridConfig.cell,
                                      ...styles.gridConfig.networkContainer,
                                      borderLeft: "0px"
                                    } :
                                  {
 ...styles.gridConfig.cell,
                                      ...styles.gridConfig.networkContainer,
                                      borderLeft: "0px"
                                    }
                              }
                            >
                              <DtvBox sx={{ ...styles.gridConfig.tr }}>
                                <DtvBox>
                                  <CardMedia
                                    alt={row?.network}
                                    component="img"
                                    loading={ props?.aboveTheFold ? "eager" : "lazy" }
                                    src={row?.logo}
                                    sx={{
                                      ...styles.gridConfig.rsnChlLogo,
                                      height: {
                                        xs: "80px",
                                        sm: "160px"
                                      },
                                      width: {
                                        xs: "80px",
                                        sm: "160px"
                                      }
                                    }}
                                  />
                                </DtvBox>
                                <DtvText
                                  content={row?.network + (row?.rsntier && row?.rsntier !== 1 ? `<sup>${row?.rsntier}</sup>` : "")}
                                  variant="body1"
                                  sx={{
                                    ...styles.jetColor,
                                    ...styles.gridConfig.networkItem,
                                    marginTop: "4px"
                                  }}
                                />
                                <DtvText
                                  content={`${row?.channelNumber ? `Channel ${row?.channelNumber}` : ""}`}
                                  variant="body1"
                                  sx={{
                                    ...styles.jetColor,
                                    ...styles.gridConfig.networkItem
                                  }}
                                />
                              </DtvBox>
                            </Paper>
                          </DtvGrid>
                          <DtvGrid item={true} sm={natVariantShowSignaturePacks ? 4 : 6} xs={5} sx={{
 maxWidth: natVariantShowSignaturePacks ? "33.33%" : "50%",
flexBasis: natVariantShowSignaturePacks ? "33.33%" : "50%"
}}>
                            <Paper
                              elevation={0}
                              square={true}
                              sx={index === 0 ?
                                {
 ...styles.gridConfig.cell,
                                    ...styles.gridConfig.teamsContainer,
                                    borderRight: "0px",
borderLeft: "0px"
                                  } :
                                index === regionalChannels.length - 1 ?
                                  {
 ...styles.gridConfig.cell,
                                      ...styles.gridConfig.teamsContainer,
                                      borderRight: "0px",
borderLeft: "0px"
                                    } :
                                  {
 ...styles.gridConfig.cell,
                                      ...styles.gridConfig.teamsContainer,
                                      borderLeft: "0px",
borderRight: "0px"
                                    }
                              }
                            >
                              <DtvBox sx={{ ...styles.gridConfig.tr }}>
                                {row?.mlb &&
                                  <DtvText
                                    content={row?.mlb}
                                    sx={{ ...styles.gridConfig.teamsItem }}
                                    variant="body2"
                                  />
                                }
                                {row?.mls &&
                                  <DtvText
                                    content={row?.mls}
                                    sx={{ ...styles.gridConfig.teamsItem }}
                                    variant="body2"
                                  />
                                }
                                {row?.nba &&
                                  <DtvText
                                    content={row?.nba}
                                    sx={{ ...styles.gridConfig.teamsItem }}
                                    variant="body2"
                                  />
                                }
                                {row?.nhl &&
                                  <DtvText
                                    content={row?.nhl}
                                    sx={{ ...styles.gridConfig.teamsItem }}
                                    variant="body2"
                                  />
                                }
                                {row?.wnba &&
                                  <DtvText
                                    content={row?.wnba}
                                    sx={{ ...styles.gridConfig.teamsItem }}
                                    variant="body2"
                                  />
                                }
                              </DtvBox>
                            </Paper>
                          </DtvGrid>
                          {natVariantShowSignaturePacks &&
                          <DtvGrid item={true} sm={4} xs={5} sx={{
 maxWidth: "33.33%",
flexBasis: "33.33%"
}}>
                            <Paper
                              elevation={0}
                              square={true}
                              sx={ index === 0 ?
                                {
 ...styles.gridConfig.cell,
                                    ...styles.gridConfig.packagesContainer,
                                    borderRight: "0px"
                                  } :
                                index === regionalChannels.length - 1 ?
                                  {
 ...styles.gridConfig.cell,
                                      ...styles.gridConfig.packagesContainer,
                                      borderRight: "0px"
                                    } :
                                  {
 ...styles.gridConfig.cell,
                                      ...styles.gridConfig.packagesContainer,
                                      borderRight: "0px"
                                    }}
                              >
                              <DtvBox>
                                <DtvBox sx={{
 display: "flex",
flexDirection: {
 xs: "column",
sm: "column",
md: "column",
lg: "row"
},
marginBottom: {
 xs: "8px",
sm: "8px",
md: "13px"
},
gap: {
 xs: "0px",
sm: "0px",
md: "0px",
lg: "10px"
}
}}>
                                  <DtvBox sx={{
 width: {
 xs: "auto",
sm: "145px",
md: "190px"
},
flex: 1,
marginBottom: {
 xs: "4px",
sm: "4px",
md: "0px"
}
}}>
                                    <DtvText content={"Signature Packages"} variant="body1" sx={{
 fontSize: {
 xs: "14px",
sm: "16px",
md: "16px"
}
}} />
                                  </DtvBox>
                                  <DtvBox sx={{
 display: "flex",
flex: 1,
flexDirection: "column",
justifyContent: "flex-start",
alignItems: "flex-start",
gap: "4px"
}} >
                                    {pkgs?.map((pkg, ix) => {
                                      const label = pkg.label.toUpperCase();
                                      return ((label === "CHOICE" || label === "PREMIER") ?
                                      <DtvButton
                                        key={ix}
                                        label={`${label}™`}
                                        mode="dark"
                                        targeturl={(isCombineChannel || (row.isStream && !row.isSatellite) ) ? pkg.hrefInternet : pkg.hrefSat}
                                        variant="uxtext"
                                        endIcon={
                                          <DtvBox component={"span"}>
                                            <DtvAvatar
                                              alt="arrow-right"
                                              src={"https://www.directv.com/dtvassets/global/icons/2022/arrow-right_lt-bg.svg"}
                                              sx={{
                                                width: "16px",
height: "16px",
                                                marginLeft: "2px",
                                                "& img": {
                                                  filter: "invert(36%) sepia(98%) saturate(412%) hue-rotate(170deg) brightness(93%) contrast(93%)"
                                                }
                                              }}
                                            />
                                          </DtvBox>
                                        }
                                        googleAnalyticsData={getAnalyticsAttrs(
                                          `${label}™`,
                                          "rsn_lookup_tool",
                                          (isCombineChannel || (row.isStream && !row.isSatellite) ) ? pkg.hrefInternet : pkg.hrefSat
                                        )}
                                        sx={{
                                          cursor: "pointer",
                                          fontSize: {
 xs: "14px",
sm: "20px",
md: "20px"
},
                                          fontStyle: "normal",
                                          textDecoration: "none",
                                          color: "#2E70BD !important",
                                          textWrap: "nowrap",
                                          "& .MuiButton-endIcon": {
                                            marginLeft: "0px"
                                          }
                                        }}
                                      />                                      :
                                      <DtvButton
                                        label={label}
                                        mode="dark"
                                        targeturl={(isCombineChannel || (row.isStream && !row.isSatellite) ) ? pkg.hrefInternet : pkg.hrefSat}
                                        variant="uxtext"
                                        endIcon={
                                          <DtvBox component={"span"}>
                                            <DtvAvatar
                                              alt="arrow-right"
                                              src={"https://www.directv.com/dtvassets/global/icons/2022/arrow-right_lt-bg.svg"}
                                              sx={{
                                                width: "16px",
height: "16px",
                                                marginLeft: "2px",
                                                "& img": {
                                                  filter: "invert(36%) sepia(98%) saturate(412%) hue-rotate(170deg) brightness(93%) contrast(93%)"
                                                }
                                              }}
                                            />
                                          </DtvBox>
                                        }
                                        googleAnalyticsData={getAnalyticsAttrs(
                                          label,
                                          "rsn_lookup_tool",
                                          (isCombineChannel || (row.isStream && !row.isSatellite) ) ? pkg.hrefInternet : pkg.hrefSat
                                        )}
                                        sx={{
                                          cursor: "pointer",
                                          fontSize: {
 xs: "14px",
sm: "20px",
md: "20px"
},
                                          fontStyle: "normal",
                                          textDecoration: "none",
                                          color: "#2E70BD !important",
                                          textWrap: "nowrap",
                                          "& .MuiButton-endIcon": {
                                            marginLeft: "0px"
                                          }
                                        }}
                                      />
                                    )
})}
                                  </DtvBox>
                                </DtvBox>
                              <DtvBox sx={{
 display: "flex",
flexDirection: {
 xs: "column",
sm: "column",
md: "column",
lg: "row"
},
alignItems: "flex-start",
alignSelf: "stretch",
gap: {
 xs: "0px",
sm: "0px",
md: "0px",
lg: "10px"
}
}}>
                                  <DtvBox sx={{
 width: {
 xs: "auto",
sm: "145px",
md: "190px"
},
flex: 1,
marginBottom: {
 xs: "4px",
sm: "4px",
md: "0px"
}
}}>
                                    <DtvText content={"Genre Packs"} variant="body1" sx={{
 fontSize: {
 xs: "14px",
sm: "16px",
md: "16px"
}
}} />
                                  </DtvBox>
                                  {(row?.packageNames?.includes("MYHOMETEAM")) ?
                                    <DtvBox sx={{
 display: "flex",
flex: 1,
flexDirection: "column",
alignItems: "flex-start"
}}>
                                      <DtvButton
                                        label={"MyHome Team"}
                                        mode="dark"
                                        targeturl={"/shop/build/configure"}
                                        variant="uxtext"
                                        endIcon={
                                          <DtvBox component={"span"}>
                                            <DtvAvatar
                                              alt="arrow-right"
                                              src={"https://www.directv.com/dtvassets/global/icons/2022/arrow-right_lt-bg.svg"}
                                              sx={{
                                                width: "16px",
height: "16px",
                                                marginLeft: "2px",
                                                "& img": {
                                                  filter: "invert(36%) sepia(98%) saturate(412%) hue-rotate(170deg) brightness(93%) contrast(93%)"
                                                }
                                              }}
                                            />
                                          </DtvBox>
                                        }
                                        googleAnalyticsData={getAnalyticsAttrs(
                                          "MyHome Team",
                                          "rsn_lookup_tool",
                                          "/shop/build/configure"
                                        )}
                                        sx={{
                                          cursor: "pointer",
                                          fontSize: {
 xs: "14px",
sm: "20px",
md: "20px"
},
                                          fontStyle: "normal",
                                          textDecoration: "none",
                                          color: "#2E70BD !important",
                                          textWrap: "nowrap",
                                          "& .MuiButton-endIcon": {
                                            marginLeft: "0px"
                                          }
                                        }}
                                      />
                                      <DtvText content={"Requires a qualifying base pack"} variant="labelText" sx={{
 fontSize: "12px",
lineHeight: "130%",
marginTop: {
 xs: "6px",
sm: "4px"
}
}} />
                                    </DtvBox>                                    :
                                    <DtvBox sx={{
 display: "flex",
flex: 1,
flexDirection: "column",
alignItems: "flex-start"
}}>
                                      <DtvText content="Not available" variant="labelText" sx={{
 fontSize: "12px",
whiteSpace: "nowrap"
}} />
                                    </DtvBox>
                                  }
                              </DtvBox>
                              </DtvBox>
                            </Paper>
                          </DtvGrid>
                          }
                        </DtvGrid>
                      )
                    })}
                  </DtvGrid>
                  <>
                    {tierTwoFlag === true &&
                      <DtvText
                        component="p"
                        cta={{ ...props?.legalTextRsnTier2?.cta }}
                        gutterBottom={true}
                        id={`${componentId}-legaltext2`}
                        sx={{ ...styles.legalTextStyles }}
                        variant="smalltext"
                        content={props?.legalTextRsnTier2?.content ?
                          props?.legalTextRsnTier2?.content :
                          props?.legalTextRsnTier2 ?
                            props?.legalTextRsnTier2 :
                            "2 For the ZIP Code entered above, Regional Sports Networks and team games are exclusive to DIRECTV Satellite as part of the ULTIMATE Package."
                                }
                        inlineLinkStyle={{
                          color: props?.legalTextRsnTier2?.cta?.color ? props?.legalTextRsnTier2?.cta?.color : "#102641",
                          fontWeight: props?.legalTextRsnTier2?.cta?.fontWeight ? props?.legalTextRsnTier2?.cta?.fontWeight : 500
                        }}
                      />
                    }
                    {tierThreeFlag === true &&
                      <DtvText
                        component="p"
                        cta={{ ...props?.legalTextRsnTier3?.cta }}
                        gutterBottom={true}
                        id={`${componentId}-legaltext3`}
                        sx={{ ...styles.legalTextStyles }}
                        variant="smalltext"
                        content={props?.legalTextRsnTier3?.content ?
                          props?.legalTextRsnTier3?.content :
                          props?.legalTextRsnTier3 ?
                            props?.legalTextRsnTier3 :
                            "3 For the ZIP Code entered above, team games are exclusive to DIRECTV Satellite as part of DIRECTV Sports Pack (with CHOICE Package and above), LO MAXIMO or PREMIER Package."
                        }
                        inlineLinkStyle={{
                          color: props?.legalTextRsnTier3?.cta?.color ? props?.legalTextRsnTier3?.cta?.color : "#102641",
                          fontWeight: props?.legalTextRsnTier3?.cta?.fontWeight ? props?.legalTextRsnTier3?.cta?.fontWeight : 500
                        }}
                      />
                    }
                  </>
                </>
              ) : (!apiError) ? (
                <DtvText
                  color="#102641"
                  component="p"
                  content={props?.noChannelResultsMsg || "DIRECTV does not offer Regional Sports Networks in your area."}
                  gutterBottom={true}
                  id="component-error"
                  variant={variant === "nationalAndRsn" ? "subhead2medium" : "smalltext"}
                  sx={{
                    fontFamily: "PFDinReg",
                    textAlign: "center",
                    mt: {
                      xs: "8px",
                      sm: "12px",
                      md: "8px"
                    },
                    mb: {
                      xs: "30px",
                      sm: "36px",
                      md: "50px"
                    }
                  }}
                />
              ) : (
                <DtvText
                  color="#102641"
                  component="p"
                  content={props?.channelSearchErrorMsg ||  "Error retrieving channel listings. Please try again later."}
                  gutterBottom={true}
                  id="component-error"
                  variant="smalltext"
                  sx={{
                    fontSize: "14px",
                    textAlign: "center",
                    fontFamily: "PFDinReg",
                    mt: {
                      xs: "8px",
                      sm: "12px",
                      md: "8px"
                    },
                    mb: {
                      xs: "30px",
                      sm: "36px",
                      md: "50px"
                    }
                  }}
                />
              )}
            </>}
          </>
          {variant === "nationalAndRsn" &&
            <DtvText
              variant={props?.nationalRsnVariantLegal?.[0]?.variant || "body1"}
              content={
                props?.nationalRsnVariantLegal?.[0]?.content ||
                "<strong>Local Channels, Teams and Regional Sports Network:</strong> Not available in select areas. Channels vary by service & billing region. <strong>Limits:</strong> Programming subject to blackout restrictions."
              }
              sx={{
                fontSize: "14px",
                color: props?.nationalRsnVariantLegal?.[0]?.color || "#102641"
              }}
            />
          }
        </>
      </DtvBox>
    );
  } catch (error) {
    return <ErrorDisplay component="RSN Lookup Tool" error={error}/>;
  }
}
